/**
 * OpenInAppBanner — top-of-page nudge that appears when a user lands
 * on a /doserne URL via the Facebook / Instagram / TikTok in-app
 * browser (or any non-standalone browser tab), telling them how to
 * get the real Doserne Academy PWA experience instead of the cramped
 * WebView.
 *
 * Behaviour:
 * - Hidden if the user is ALREADY in the installed PWA (standalone
 *   display-mode or iOS `navigator.standalone`).
 * - Hidden after the user dismisses it once (localStorage flag).
 * - Language auto-detected from `localStorage.DOSERNE_LANG` (if the
 *   user has picked one before) else from `navigator.language` with a
 *   sensible fallback to English. So a Romanian phone sees Romanian,
 *   a Norwegian phone sees Norwegian, everyone else sees English.
 * - Detects Facebook / Instagram / TikTok in-app browsers via UA and
 *   gives them a direct "Open in Chrome/Safari" nudge — those
 *   browsers don't honour PWAs.
 * - Detects iOS / Android / desktop and shows the correct install
 *   instructions or the Chrome deferred-install prompt.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, ExternalLink, Smartphone, Copy, Check } from "lucide-react";

const DISMISS_KEY = "DOSERNE_OPEN_IN_APP_DISMISSED_V1";

const COPY = {
  no: {
    title: "Åpne i Doserne Academy-appen",
    inapp_msg: "Du er inne i Facebook-nettleseren. For en bedre opplevelse, åpne denne siden i en ekte nettleser eller i den installerte appen.",
    installed_msg: "Du har allerede Doserne Academy installert. Trykk under for å åpne appen direkte.",
    android_msg: "Installer Doserne Academy som app for varsler, chat og offline-tilgang.",
    ios_msg: "På iPhone: trykk Del-knappen ↑, så «Legg til på Hjem-skjerm» for å installere Doserne som app.",
    desktop_msg: "Installer Doserne Academy på PC-en din for rask tilgang.",
    open_chrome: "Åpne i Chrome",
    open_safari: "Åpne i Safari",
    open_app: "Åpne appen",
    install: "Installer",
    copy_link: "Kopier lenke",
    copied: "Kopiert!",
    later: "Ikke nå",
    ios_share_hint: "1. Trykk delings-knappen ⬆️ nederst i Safari\n2. Bla ned og trykk «Legg til på Hjem-skjerm»",
    fb_ios_hint: "Trykk «…» øverst til høyre og velg «Åpne i Safari»",
    fb_android_hint: "Trykk «⋮» øverst til høyre og velg «Åpne i Chrome»",
  },
  ro: {
    title: "Deschide în aplicația Doserne Academy",
    inapp_msg: "Ești în browser-ul intern Facebook. Pentru o experiență mai bună, deschide această pagină în browser real sau în aplicația instalată.",
    installed_msg: "Ai deja Doserne Academy instalată. Apasă mai jos pentru a deschide direct aplicația.",
    android_msg: "Instalează Doserne Academy ca aplicație pentru notificări, chat și acces offline.",
    ios_msg: "Pe iPhone: apasă butonul Share ↑, apoi «Adaugă pe ecranul principal» pentru a instala Doserne ca aplicație.",
    desktop_msg: "Instalează Doserne Academy pe PC pentru acces rapid.",
    open_chrome: "Deschide în Chrome",
    open_safari: "Deschide în Safari",
    open_app: "Deschide aplicația",
    install: "Instalează",
    copy_link: "Copiază link",
    copied: "Copiat!",
    later: "Nu acum",
    ios_share_hint: "1. Apasă butonul Share ⬆️ jos în Safari\n2. Derulează și apasă «Adaugă pe ecranul principal»",
    fb_ios_hint: "Apasă «…» în dreapta sus și alege «Deschide în Safari»",
    fb_android_hint: "Apasă «⋮» în dreapta sus și alege «Deschide în Chrome»",
  },
  en: {
    title: "Open in the Doserne Academy app",
    inapp_msg: "You're in the Facebook in-app browser. For a better experience, open this page in a real browser or in the installed app.",
    installed_msg: "You already have Doserne Academy installed. Tap below to open the app directly.",
    android_msg: "Install Doserne Academy as an app for push notifications, chat and offline access.",
    ios_msg: "On iPhone: tap the Share button ↑, then «Add to Home Screen» to install Doserne as an app.",
    desktop_msg: "Install Doserne Academy on your PC for quick access.",
    open_chrome: "Open in Chrome",
    open_safari: "Open in Safari",
    open_app: "Open app",
    install: "Install",
    copy_link: "Copy link",
    copied: "Copied!",
    later: "Not now",
    ios_share_hint: "1. Tap the Share button ⬆️ at the bottom of Safari\n2. Scroll and tap «Add to Home Screen»",
    fb_ios_hint: "Tap «…» top-right and choose «Open in Safari»",
    fb_android_hint: "Tap «⋮» top-right and choose «Open in Chrome»",
  },
};

function pickLang() {
  try {
    const saved = localStorage.getItem("DOSERNE_LANG");
    if (saved && COPY[saved]) return saved;
  } catch { /* ignore */ }
  const nav = (typeof navigator !== "undefined" ? navigator.language : "en").toLowerCase();
  if (nav.startsWith("ro")) return "ro";
  if (nav.startsWith("nb") || nav.startsWith("nn") || nav.startsWith("no") || nav.startsWith("da") || nav.startsWith("sv")) return "no";
  return "en";
}

function detectContext() {
  if (typeof window === "undefined") return { hidden: true };
  const ua = navigator.userAgent || "";
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (isStandalone) return { hidden: true }; // already in PWA
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isFacebook = /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i.test(ua);
  const isInstagram = /Instagram/i.test(ua);
  const isTikTok = /TikTok|BytedanceWebview/i.test(ua);
  const isInApp = isFacebook || isInstagram || isTikTok;
  return {
    hidden: false,
    isIOS, isAndroid, isInApp,
    isDesktop: !isIOS && !isAndroid,
  };
}

export default function OpenInAppBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [ctx, setCtx] = useState({ hidden: true });
  const [lang, setLang] = useState("en");
  const [copied, setCopied] = useState(false);
  const [deferredInstall, setDeferredInstall] = useState(null);
  const [installedRelatedApp, setInstalledRelatedApp] = useState(false);

  const t = useMemo(() => COPY[lang] || COPY.en, [lang]);

  useEffect(() => {
    setLang(pickLang());
    setCtx(detectContext());
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* ignore */ }
    // Chrome/Edge PWA install prompt.
    const onBip = (ev) => {
      ev.preventDefault();
      setDeferredInstall(ev);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    // Detect if the PWA is already installed (Chromium only).
    if (navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps()
        .then((apps) => setInstalledRelatedApp(apps && apps.length > 0))
        .catch(() => {});
    }
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  if (ctx.hidden || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback: select fails on some in-apps */ }
  };

  const doInstall = async () => {
    if (!deferredInstall) return;
    try {
      deferredInstall.prompt();
      const { outcome } = await deferredInstall.userChoice;
      if (outcome === "accepted") dismiss();
    } catch { /* ignore */ }
    setDeferredInstall(null);
  };

  // Decide the main message + primary CTA per context.
  let message;
  let hint = "";
  if (ctx.isInApp) {
    message = t.inapp_msg;
    hint = ctx.isIOS ? t.fb_ios_hint : t.fb_android_hint;
  } else if (installedRelatedApp) {
    message = t.installed_msg;
  } else if (ctx.isIOS) {
    message = t.ios_msg;
    hint = t.ios_share_hint;
  } else if (ctx.isAndroid) {
    message = t.android_msg;
  } else {
    message = t.desktop_msg;
  }

  return (
    <div
      className="fixed top-0 inset-x-0 z-[70] bg-gradient-to-r from-cyan-500 to-emerald-500 shadow-lg"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      data-testid="open-in-app-banner"
    >
      <div className="max-w-6xl mx-auto px-3 py-2.5 flex items-start gap-3" style={{ color: "#fff" }}>
        <div className="w-9 h-9 rounded-lg bg-white/25 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Smartphone className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black leading-tight" style={{ color: "#fff" }}>{t.title}</div>
          <div className="text-[11px] leading-snug mt-0.5 opacity-95" style={{ color: "#fff" }}>{message}</div>
          {hint && (
            <div className="text-[11px] leading-snug mt-1 opacity-95 whitespace-pre-line bg-white/15 rounded-md px-2 py-1" style={{ color: "#fff" }}>
              {hint}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {/* Chrome install prompt when available */}
            {deferredInstall && (
              <button
                onClick={doInstall}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white text-emerald-700 text-xs font-black shadow-sm active:scale-95"
                data-testid="banner-install-btn"
              >
                <ExternalLink className="w-3 h-3" /> {t.install}
              </button>
            )}
            {/* Copy link so user can paste in Chrome/Safari */}
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/20 hover:bg-white/30 text-xs font-black active:scale-95"
              style={{ color: "#fff" }}
              data-testid="banner-copy-link"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? t.copied : t.copy_link}
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="w-8 h-8 rounded-lg hover:bg-white/20 flex items-center justify-center flex-shrink-0"
          style={{ color: "#fff" }}
          aria-label={t.later}
          data-testid="banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
